import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { models as modelsApi } from "../../lib/api";
import type { ModelChoice } from "../../lib/types";
import { decodeModel, encodeModel } from "../../lib/modelValue";
import { Brain, Send, Slash } from "../shared/Icon";
import { cn } from "../../lib/cn";

const SLASH_COMMANDS = [
  { name: "/status", hint: "Show live task status" },
  { name: "/cancel", hint: "Cancel a running task" },
  { name: "/retry", hint: "Retry the last attempt" },
  { name: "/cost", hint: "Spend summary by project" },
  { name: "/clear", hint: "Clear the chat" },
] as const;

interface Props {
  onSend: (text: string) => void;
  disabled?: boolean;
  attribution?: string;
  pending?: boolean;
  model?: string;
  onModelChange?: (model: string) => void;
}

export function Composer({
  onSend,
  disabled,
  attribution,
  pending,
  model = "",
  onModelChange,
}: Props) {
  const [text, setText] = useState("");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteIdx, setPaletteIdx] = useState(0);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const { data: modelCatalog } = useQuery({
    queryKey: ["models"],
    queryFn: modelsApi.list,
  });
  // Chat can run on any catalog model — Claude (local `claude --print`) or
  // Codex (local `codex exec`, routed by the `codex:` prefix).
  const chatModels = modelCatalog?.models ?? [];

  // Auto-resize
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "0";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [text]);

  // Open palette when input starts with /
  useEffect(() => {
    setPaletteOpen(text.startsWith("/"));
    setPaletteIdx(0);
  }, [text]);

  const filteredCommands = SLASH_COMMANDS.filter((c) =>
    c.name.startsWith(text.split(" ")[0] || "/"),
  );

  const submit = () => {
    if (!text.trim() || disabled) return;
    onSend(text.trim());
    setText("");
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (paletteOpen && filteredCommands.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setPaletteIdx((i) => (i + 1) % filteredCommands.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setPaletteIdx(
          (i) => (i - 1 + filteredCommands.length) % filteredCommands.length,
        );
        return;
      }
      if (e.key === "Tab") {
        e.preventDefault();
        setText(filteredCommands[paletteIdx].name + " ");
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="px-3 pb-3 pt-2 bg-bg-panel">
      <div className="relative">
        {paletteOpen && filteredCommands.length > 0 && (
          <div
            className={cn(
              "absolute bottom-[calc(100%+4px)] left-0 right-0 z-20",
              "bg-bg-surface rounded-md border border-border-subtle shadow-md py-1",
            )}
          >
            <div className="px-3 py-1 text-caption text-text-tertiary">
              Slash commands
            </div>
            {filteredCommands.map((c, i) => (
              <button
                key={c.name}
                onClick={() => {
                  setText(c.name + " ");
                  taRef.current?.focus();
                }}
                className={cn(
                  "w-full text-left flex items-center justify-between gap-2 px-3 py-1.5",
                  "transition-colors duration-fast ease-out",
                  i === paletteIdx
                    ? "bg-accent-soft text-text-primary"
                    : "text-text-secondary hover:bg-bg-inset",
                )}
              >
                <span className="text-mono text-[13px] text-text-primary">
                  {c.name}
                </span>
                <span className="text-small text-text-tertiary">{c.hint}</span>
              </button>
            ))}
          </div>
        )}
        <div
          className={cn(
            "flex items-end gap-1.5 rounded-lg p-2",
            "bg-bg-surface border border-border-subtle",
            "focus-within:border-border-strong transition-colors duration-fast ease-out",
            "shadow-sm",
          )}
        >
          <span
            className={cn(
              "h-7 w-7 inline-flex items-center justify-center rounded-md shrink-0",
              "text-text-tertiary",
            )}
            aria-hidden="true"
            title="Type / for commands"
          >
            <Slash />
          </span>
          <textarea
            ref={taRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            placeholder={
              attribution
                ? `Reply about ${attribution}…`
                : "Ask the managing agent…"
            }
            rows={1}
            disabled={disabled}
            className={cn(
              "flex-1 min-h-[28px] max-h-[160px] resize-none outline-none bg-transparent",
              "text-body text-text-primary placeholder:text-text-quaternary",
              "py-1.5",
            )}
          />
          <button
            onClick={submit}
            disabled={!text.trim() || disabled || pending}
            aria-label="Send"
            className={cn(
              "h-8 px-2.5 inline-flex items-center justify-center rounded-md shrink-0",
              "transition-colors duration-fast ease-out",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-panel",
              text.trim() && !disabled
                ? "bg-accent text-text-on-accent hover:bg-accent-hover"
                : "bg-bg-inset text-text-tertiary cursor-not-allowed",
            )}
          >
            <Send />
          </button>
        </div>
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1 text-caption text-text-tertiary truncate">
          {attribution ? (
            <>
              Replying about{" "}
              <span className="text-text-secondary">{attribution}</span>
            </>
          ) : (
            <span>All projects</span>
          )}
        </div>
        {onModelChange && (
          <ChatModelSelect
            value={model}
            models={chatModels}
            disabled={disabled || pending}
            onChange={onModelChange}
          />
        )}
      </div>
    </div>
  );
}

// Provider-prefixed model value (no effort) used as the model dropdown's
// option value, e.g. "claude-opus-4-8" or "codex:gpt-5.5".
function modelOptionValue(m: ModelChoice): string {
  return m.cli_binary === "claude" ? m.id : `${m.cli_binary}:${m.id}`;
}

function ChatModelSelect({
  value,
  models,
  disabled,
  onChange,
}: {
  value: string;
  models: ModelChoice[];
  disabled?: boolean;
  onChange: (model: string) => void;
}) {
  const decoded = decodeModel(value);
  // The encoded model value with effort stripped, to match an option value.
  const modelOnly = encodeModel({
    provider: decoded.provider,
    model: decoded.model,
    effort: "",
  });
  const selected = models.find((m) => modelOptionValue(m) === modelOnly);
  const efforts = selected?.efforts ?? [];
  const selectClasses = cn(
    "h-7 rounded-md bg-bg-inset px-2 text-caption",
    "text-text-secondary border border-border-subtle outline-none",
    "hover:border-border-default focus-visible:ring-2 focus-visible:ring-accent",
    "disabled:cursor-not-allowed disabled:text-text-quaternary",
  );

  const onModel = (next: string) => {
    const d = decodeModel(next);
    const opts =
      models.find((m) => modelOptionValue(m) === next)?.efforts ?? [];
    onChange(
      encodeModel({
        provider: d.provider,
        model: d.model,
        effort: opts.includes(decoded.effort) ? decoded.effort : "",
      }),
    );
  };
  const onEffort = (e: string) =>
    onChange(
      encodeModel({ provider: decoded.provider, model: decoded.model, effort: e }),
    );

  return (
    <div className="shrink-0 inline-flex items-center gap-1 text-text-tertiary">
      <Brain />
      <select
        value={modelOnly}
        onChange={(e) => onModel(e.target.value)}
        disabled={disabled}
        title="Chat model"
        aria-label="Chat model"
        className={cn(selectClasses, "max-w-[170px]")}
      >
        <option value="">Claude default</option>
        {models.map((m) => (
          <option key={modelOptionValue(m)} value={modelOptionValue(m)}>
            {m.label}
            {m.default ? " (default)" : ""}
          </option>
        ))}
      </select>
      {efforts.length > 0 && (
        <select
          value={decoded.effort}
          onChange={(e) => onEffort(e.target.value)}
          disabled={disabled}
          title="Reasoning effort"
          aria-label="Reasoning effort"
          className={cn(selectClasses, "max-w-[96px]")}
        >
          <option value="">effort</option>
          {efforts.map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
